import { createFileRoute } from "@tanstack/react-router";
import { MapPin, Clock, Smartphone, ShieldCheck, BarChart3, Users } from "lucide-react";
import { SolutionLanding } from "@/components/landing/SolutionLanding";

const URL = "https://busditeads.com/software-de-control-horario-para-construccion";
const TITLE = "Software de Control Horario con GPS para Construcción — Business Dite Ads";
const DESC =
  "Control horario con GPS para construcción: fichaje móvil, geolocalización, partes precisos y exportación lista para nómina. Prueba gratis 14 días.";

const faqs = [
  {
    q: "¿Cómo funciona el fichaje con GPS?",
    a: "El trabajador abre un enlace en su móvil y ficha. La ubicación se captura al entrar y al salir para confirmar que estuvo en la obra — sin instalar app.",
  },
  {
    q: "¿Cómo evito que fichen por otro?",
    a: "Cada trabajador tiene un token único, el GPS se registra en cada fichaje y el sistema avisa cuando la salida ocurre antes del turno habitual.",
  },
  {
    q: "¿Exporta a nómina?",
    a: "Sí. Las horas se resumen por empleado y período y alimentan directamente la nómina — con soporte para pago por hora y por día.",
  },
  {
    q: "¿Funciona sin conexión?",
    a: "La página del trabajador funciona en cualquier móvil con navegador. Los fichajes se sincronizan cuando el dispositivo vuelve a estar online.",
  },
];

export const Route = createFileRoute("/software-de-control-horario-para-construccion")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:url", content: URL },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: "es_ES" },
    ],
    links: [
      { rel: "canonical", href: URL },
      {
        rel: "alternate",
        hrefLang: "en",
        href: "https://busditeads.com/construction-time-tracking-software",
      },
      {
        rel: "alternate",
        hrefLang: "pt",
        href: "https://busditeads.com/software-de-controle-de-ponto-para-construcao",
      },
      { rel: "alternate", hrefLang: "es", href: URL },
      {
        rel: "alternate",
        hrefLang: "x-default",
        href: "https://busditeads.com/construction-time-tracking-software",
      },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          inLanguage: "es",
          mainEntity: faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <SolutionLanding
      eyebrow="Control Horario para Construcción"
      h1="Control horario con GPS que tu cuadrilla sí va a usar"
      subhead="Fichaje móvil, GPS en la obra, partes precisos y exportación lista para nómina — diseñado para contratistas de albañilería, techado, concreto y generales."
      primaryCtaLabel="Empezar prueba gratis de 14 días"
      secondaryCtaLabel="Ver precios"
      benefits={[
        "Funciona en cualquier móvil",
        "Sin instalar app",
        "GPS en cada fichaje",
        "Cancela cuando quieras",
      ]}
      featuresHeading="Pensado para la realidad de la obra"
      features={[
        {
          icon: Smartphone,
          title: "Fichaje móvil",
          description:
            "La cuadrilla abre un enlace seguro y ficha. Sin instalaciones, sin app en la tienda, sin TI.",
        },
        {
          icon: MapPin,
          title: "Horas verificadas por GPS",
          description:
            "Captura la ubicación al entrar y al salir. Detecta al instante fichajes fuera de la obra.",
        },
        {
          icon: Clock,
          title: "Partes precisos",
          description:
            "Cálculo automático de turno, descansos y confirmaciones para evitar fichajes accidentales.",
        },
        {
          icon: BarChart3,
          title: "Costo de mano de obra por obra",
          description:
            "Las horas se asocian a proyectos para que veas el costo real de mano de obra por obra, en tiempo real.",
        },
        {
          icon: Users,
          title: "Cuadrillas y roles",
          description: "Agrega empleados y subcontratistas, asigna roles y controla quién ve qué.",
        },
        {
          icon: ShieldCheck,
          title: "Registros auditables",
          description:
            "Historial inmutable de fichajes, log de edición y exportaciones para auditorías y disputas.",
        },
      ]}
      howItWorksHeading="Del fichaje a la nómina en tres pasos"
      steps={[
        {
          title: "Envía el enlace a la cuadrilla",
          description:
            "Genera un enlace seguro por trabajador — envíalo por SMS, WhatsApp o correo.",
        },
        {
          title: "Los trabajadores fichan con GPS",
          description:
            "Cada fichaje queda con hora y ubicación, asociado al trabajador y a la obra.",
        },
        {
          title: "Aprueba y paga",
          description:
            "Revisa los partes, corrige excepciones y corre la nómina con las horas ya cargadas.",
        },
      ]}
      socialProofHeading="Por qué los contratistas eligen nuestro control horario"
      socialProof={[
        "Reduce horas de preparar la nómina a minutos",
        "Deja de pagar horas que no se trabajaron",
        "Sabe exactamente qué cuadrilla está en qué obra",
        "Detecta salidas olvidadas con alertas inteligentes",
        "Soporta pago por hora y por día",
        "Disponible en español, portugués e inglés",
      ]}
      faqHeading="Preguntas frecuentes"
      faqs={faqs}
      finalCtaHeading="Deja de perseguir partes de horas. Empieza a medir horas reales."
      finalCtaSubhead="Prueba Business Dite Ads gratis 14 días — pon a toda la cuadrilla a fichar desde el primer día."
    />
  );
}
